import React, { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Box, Skeleton, useTheme } from '@mui/material';
import { formatFloat } from '../../../../helpers';
import { formatMoney } from './expenses-summary-delta';
import { SummaryPoint } from './expenses-summary-model';

// Same family the production chart uses, extended — one color per stacked
// category. "Otros" always takes the last (grey) slot.
const COLORS = [
    '#1976d2',
    '#388e3c',
    '#f57c00',
    '#7b1fa2',
    '#d32f2f',
    '#0288d1',
];
const OTHERS_COLOR = '#9e9e9e';
const OTHERS_LABEL = 'Otros';

// More than a handful of stacked series stops being readable, so only the
// biggest categories over the window get their own band; the rest are summed
// into "Otros" — the stack total stays exact either way.
const MAX_SERIES = 6;

const MONTH_ABBREVIATIONS = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
];

// The 12 months ending at (and including) the selected one. Spans two calendar
// years, which is exactly why the page fetches the previous year too.
function trailingTwelveMonths(year: number, month: number) {
    const months: { year: number; month: number }[] = [];
    for (let offset = 11; offset >= 0; offset--) {
        // Convert to an absolute month index, step, and convert back — avoids
        // any date arithmetic and its end-of-month traps.
        const absolute = year * 12 + (month - 1) - offset;
        months.push({
            year: Math.floor(absolute / 12),
            month: (absolute % 12) + 1,
        });
    }
    return months;
}

function renderTooltip(
    bg: string,
    border: string,
    textColor: string,
    secondaryColor: string,
) {
    return function TooltipContent(props: any) {
        const { active, payload, label } = props;
        if (!active || !payload?.length) return null;
        // Recharts hands the stack over bottom-up; reading it top-down matches
        // what the eye sees in the bar.
        const entries = [...payload].reverse().filter((e: any) => e.value > 0);
        const total = payload.reduce(
            (acc: number, entry: any) => acc + (entry.value || 0),
            0,
        );
        return (
            <div
                style={{
                    backgroundColor: bg,
                    border: `1px solid ${border}`,
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 12,
                    color: textColor,
                }}
            >
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                    {label}
                </div>
                {entries.map((entry: any, i: number) => (
                    <div
                        key={i}
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                        }}
                    >
                        <span style={{ color: entry.color }}>■</span>
                        <span>{entry.name}:</span>
                        <span style={{ fontWeight: 'bold' }}>
                            {formatMoney(entry.value)}
                        </span>
                    </div>
                ))}
                <div
                    style={{
                        marginTop: 4,
                        paddingTop: 4,
                        borderTop: `1px solid ${border}`,
                        color: secondaryColor,
                    }}
                >
                    Total:{' '}
                    <span style={{ fontWeight: 'bold', color: textColor }}>
                        {formatMoney(total)}
                    </span>
                </div>
            </div>
        );
    };
}

function renderLegend(textColor: string) {
    return function LegendContent(props: any) {
        const { payload } = props;
        if (!payload) return null;
        return (
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '4px 16px',
                    fontSize: 12,
                }}
            >
                {payload.map((entry: any, index: number) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <svg width={10} height={10}>
                            <rect width={10} height={10} fill={entry.color} />
                        </svg>
                        <span style={{ color: textColor, fontSize: 12 }}>
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    };
}

export function ExpensesSummaryTrendChart({
    points,
    year,
    month,
    loading,
}: {
    points: SummaryPoint[];
    year: number;
    // 1-indexed (backend convention), the month under review.
    month: number;
    loading?: boolean;
}) {
    const theme = useTheme();

    const { chartData, series } = useMemo(() => {
        const monthsWindow = trailingTwelveMonths(year, month);
        const inWindow = points.filter((point) =>
            monthsWindow.some(
                (ref) => ref.year === point.year && ref.month === point.month,
            ),
        );

        // Rank categories by their total across the whole window, so the bands
        // stay in the same order (and keep the same color) month to month.
        const totalsByName = new Map<string, number>();
        inWindow.forEach((point) => {
            totalsByName.set(
                point.name,
                (totalsByName.get(point.name) ?? 0) + point.total,
            );
        });
        // Array.from, not a spread: the build targets es5 without
        // downlevelIteration, so spreading a Map iterator does not compile.
        const ranked = Array.from(totalsByName.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
        const top = ranked.slice(0, MAX_SERIES);
        const hasOthers = ranked.length > top.length;
        const seriesNames = hasOthers ? [...top, OTHERS_LABEL] : top;

        const data = monthsWindow.map((ref) => {
            const bucket: Record<string, number | string | boolean> = {
                label: `${MONTH_ABBREVIATIONS[ref.month - 1]} ${String(
                    ref.year,
                ).slice(2)}`,
                // Drives the highlight: every other month renders faded.
                selected: ref.year === year && ref.month === month,
            };
            seriesNames.forEach((name) => {
                bucket[name] = 0;
            });
            inWindow
                .filter(
                    (point) =>
                        point.year === ref.year && point.month === ref.month,
                )
                .forEach((point) => {
                    const key = top.includes(point.name)
                        ? point.name
                        : OTHERS_LABEL;
                    bucket[key] = (bucket[key] as number) + point.total;
                });
            return bucket;
        });

        return { chartData: data, series: seriesNames };
    }, [points, year, month]);

    if (loading && points.length === 0) {
        return (
            <Skeleton variant={'rectangular'} height={320} sx={{ borderRadius: 1 }} />
        );
    }

    return (
        <Box sx={{ height: 340, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{
                            fontSize: 12,
                            fill: theme.palette.text.primary,
                        }}
                    />
                    <YAxis
                        tick={{
                            fontSize: 12,
                            fill: theme.palette.text.primary,
                        }}
                        tickFormatter={(value) => formatFloat(value, 0)}
                        width={80}
                    />
                    <Tooltip
                        cursor={{ fill: theme.palette.action.hover }}
                        content={renderTooltip(
                            theme.palette.background.paper,
                            theme.palette.divider,
                            theme.palette.text.primary,
                            theme.palette.text.secondary,
                        )}
                    />
                    <Legend content={renderLegend(theme.palette.text.primary)} />
                    {series.map((name, index) => {
                        const color =
                            name === OTHERS_LABEL
                                ? OTHERS_COLOR
                                : COLORS[index % COLORS.length];
                        return (
                            <Bar
                                key={name}
                                dataKey={name}
                                name={name}
                                stackId={'expenses'}
                                fill={color}
                                maxBarSize={48}
                            >
                                {/* Per-cell opacity is the only way to fade a
                                    single month within a stacked series. */}
                                {chartData.map((entry, entryIndex) => (
                                    <Cell
                                        key={entryIndex}
                                        fillOpacity={entry.selected ? 1 : 0.45}
                                    />
                                ))}
                            </Bar>
                        );
                    })}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
